/**
 *
 * @module commerceApi
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/CommerceApi.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/commerce.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import CommerceManager from './commerceManager';
import SubmitOrderRequest from './submitOrderRequest';
import SubmitOrderWithPaymentMethodRequest from './submitOrderWithPaymentMethodRequest';
import PaymentMethodInformation from './paymentMethodInformation';

const Util = Services.Util;
const TokenManager = Token.TokenManager;
const AmountedLineItem = Services.Commerce.AmountedLineItem;
const PassThroughData = Services.Commerce.PassThroughData;
const PriceOrderRequest = Services.Commerce.PriceOrderRequest;
const PriceOrderResponse = Services.Commerce.PriceOrderResponse;
const SubmitServiceOrderRequest = Services.Commerce.SubmitOrderRequest;
const OrderStatusResponse = Services.Commerce.OrderStatusResponse;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const CommerceApiDustUrnReference = Services.Internal.Dust.DustUrnReference.commerce.commerceApi;
const CreatePaymentMethodRequest = Services.Commerce.CreatePaymentMethodRequest;
const CreatePaymentMethodPassthroughData = Services.Commerce.CreatePaymentMethodPassthroughData;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class CommerceApi
 * @desc Provides ability to access commerce data and link subscriptions to the commerce.
 *
 */
export default class CommerceApi {

    /**
     *
     * @param {CommerceManager} commerceManager
     * @param {SDK.Token.TokenManager} tokenManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(commerceManager, tokenManager, logger = Logging) {

        const errorMessage = {
            ctor: `${this.constructor.name}(commerceManager, tokenManager, logger)`,
            get commerceManager() {
                return `${this.ctor} needs SDK.Commerce.CommerceManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            }
        };

        instanceStrict(commerceManager, CommerceManager, errorMessage.commerceManager, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Commerce.CommerceManager}
         *
         */
        this.commerceManager = commerceManager;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.commerceManager.client.dustEnabled);

        this.logger.info(CommerceApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {PriceOrderRequest} request - A description of products to be priced.
     * May include location information for results with taxes calculated.
     * @desc Gets information that can be used to display product pricing to users. Cannot submit an
     * order without this information. If user's location is provided, tax will be calculated.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Commerce.PriceOrderResponse>} A promise that completes
     * when the operation has succeeded and returns pricing information for an order, given products,
     * discount vouchers, and (optionally) the user's location.
     *
     */
    priceOrder(request) {

        const errorMessage = {
            method: `${this.constructor.name}.priceOrder(request)`,
            get request() {
                return `${this.method} needs SDK.Services.Commerce.PriceOrderRequest instance`;
            }
        };

        const { commerceManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceApi.name,
            urn: CommerceApiDustUrnReference.priceOrder
        });

        instanceStrict(request, PriceOrderRequest, errorMessage.request, InvalidArgumentException);

        return commerceManager.priceOrder(request).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} zipCode - The zip code to be used for looking up potential cities and states.
     * @desc Gets a list of potential cities and states that can be used as a billing address.
     * @throws {InvalidZipCodeException} Invalid or nonexistent zip code. E.g. `asdf` or `00000` would trigger this.
     * @note Response of HTTP 403.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Array<SDK.Services.Commerce.ZipLocation>>} A promise that completes when the operation
     * has succeeded and returns address information for potential cities and states based on the given zip code.
     *
     */
    getLocationCandidates(zipCode) {

        const errorMessage = {
            method: `${this.constructor.name}.getLocationCandidates(zipCode)`,
            get zipCode() {
                return `${this.method} zipCode should be a String with length`;
            }
        };

        const { commerceManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceApi.name,
            urn: CommerceApiDustUrnReference.getLocationCandidates
        });

        nonEmptyString(zipCode, errorMessage.zipCode, InvalidArgumentException);

        return commerceManager.lookupByZipCode(zipCode).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Commerce.SubmitOrderRequest} request - A description of products to be
     * ordered and information necessary to process the order.
     * @desc Submits an order for purchasing. If the order is successfully placed, a unique
     * identifier for the order is returned.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<String>} A promise that completes when the operation has
     * succeeded and returns a unique identifier for the order.
     *
     */
    submitOrder(request) {
        const errorMessage = {
            method: `${this.constructor.name}.submitOrder(request)`,
            get request() {
                return `${this.method} needs SDK.Services.Commerce.SubmitOrderRequest instance`;
            }
        };

        const { commerceManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceApi.name,
            urn: CommerceApiDustUrnReference.submitOrder
        });

        instanceStrict(request, SubmitOrderRequest, errorMessage.request, InvalidArgumentException);

        const { tokenManager, processLineItems } = this;
        const { lineItems, paymentInformation, orderCampaign, locale } = request;
        const { creditCardNumber, addressableCreditCard, currency } = paymentInformation;

        const accessToken = tokenManager.getAccessToken();
        const amountedLineItems = processLineItems(lineItems);
        const appAccount = Util.getSafe(() => commerceManager.client.config.extras.appAccount);
        const clientId = Util.getSafe(() => commerceManager.client.config.extras.clientId);
        const fingerPrint = accessToken.token;
        const orderCampaigns = [orderCampaign];

        return commerceManager.getGuid().then((guid) => {

            const passThroughData = new PassThroughData({
                guid,
                fingerPrint,
                addressableCreditCard,
                appAccount,
                currency,
                lineItems: amountedLineItems,
                locale,
                orderCampaigns
            });

            const submitOrderRequest = new SubmitServiceOrderRequest(
                creditCardNumber,
                clientId,
                passThroughData
            );

            return commerceManager.submitOrder(submitOrderRequest).then((orderId) => {

                logger.info(CommerceApi.name, 'Order submitted successfully.');

                return Promise.resolve(orderId);

            });

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {SDK.Commerce.SubmitOrderWithPaymentMethodRequest} request - A description
     * of products to be ordered and information necessary to process the order.
     * @desc Submits an order for purchasing. If the order is successfully placed, a unique
     * identifier for the order is returned.
     * @throws {InvalidDataException} Unable to submit the order due to invalid payment information.
     * @note Request to submitOrderWithPaymentMethod returned HTTP 400.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<String>} A promise that completes when the operation has succeeded
     * and returns a unique identifier for the order.
     *
     */
    submitOrderWithPaymentMethod(request) {

        const errorMessage = {
            ctor: `${this.constructor.name}.submitOrderWithPaymentMethod(request)`,
            get request() {
                return `${this.ctor} needs SDK.Commerce.SubmitOrderWithPaymentMethodRequest instance`;
            }
        };

        instanceStrict(request, SubmitOrderWithPaymentMethodRequest, errorMessage.request, InvalidArgumentException);

        const { commerceManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceApi.name,
            urn: CommerceApiDustUrnReference.submitOrderWithPaymentMethod
        });

        return commerceManager.submitOrderWithPaymentMethod(request).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} orderId - The identifier for the order.
     * @desc Checks the status of the order specified by the passed in guid. Returns true if the order is complete.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Boolean>} A promise that completes when the operation has succeeded and
     * returns an indication of whether or not the order was successfully processed.
     *
     */
    checkOrderStatus(orderId) {

        const errorMessage = {
            method: `${this.constructor.name}.checkOrderStatus(orderId)`,
            get orderId() {
                return `${this.method} orderId should be a String with length`;
            }
        };

        const { commerceManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceApi.name,
            urn: CommerceApiDustUrnReference.checkOrderStatus
        });

        nonEmptyString(orderId, errorMessage.orderId, InvalidArgumentException);

        return commerceManager.getOrderStatus(orderId).then((orderStatus) => {

            const isOrderComplete = Check.equal(orderStatus, OrderStatusResponse.Success);

            if (isOrderComplete) {
                logger.info(CommerceApi.name, `Order complete for guid: "${orderId}".`);
            }

            return Promise.resolve(isOrderComplete);

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {SDK.Commerce.PaymentMethodInformation} paymentInformation - info about the payment method to create.
     * @param {SDK.Services.Commerce.PriceOrderResponse} priceOrderResponse - Information retrieved from a call to
     * `SDK.Commerce.CommerceApi.PriceOrder()`.
     * @desc Creates a new payment method which case be used later to submit an order.
     * @throws {InvalidPaymentInformationException} Unable to create payment information due to invalid data.
     * @note Request to createPaymentMethod returned HTTP 422.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<String>} The payment method ID for the payment that was added.
     *
     */
    createPaymentMethod(paymentInformation, priceOrderResponse) {

        const errorMessage = {
            ctor: `${this.constructor.name}.createPaymentMethod(paymentInformation, priceOrderResponse)`,
            get paymentInformation() {
                return `${this.ctor} needs SDK.Commerce.PaymentMethodInformation instance`;
            },
            get priceOrderResponse() {
                return `${this.ctor} needs SDK.Services.Commerce.PriceOrderResponse instance`;
            }
        };

        instanceStrict(
            paymentInformation,
            PaymentMethodInformation,
            errorMessage.paymentInformation,
            InvalidArgumentException
        );

        instanceStrict(
            priceOrderResponse,
            PriceOrderResponse,
            errorMessage.priceOrderResponse,
            InvalidArgumentException
        );

        const { commerceManager, dustEnabled, logger } = this;
        const { creditCardNumber } = paymentInformation;
        const { region } = priceOrderResponse;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceApi.name,
            urn: CommerceApiDustUrnReference.createPaymentMethod
        });

        const namespaceId = Util.getSafe(() => commerceManager.client.config.extras.appAccount.namespaceId);
        const passThroughData = new CreatePaymentMethodPassthroughData(paymentInformation);

        const createPaymentMethodRequest = new CreatePaymentMethodRequest(
            creditCardNumber,
            namespaceId,
            passThroughData
        );

        return commerceManager.createPaymentMethod(createPaymentMethodRequest, region).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @desc Retrieves the default payment method for the user which case be used to submit an order.
     * @throws {PaymentMethodNotFoundException} Unable to retrieve the payment method as it was not found.
     * @note Request to perform operation returned HTTP 404.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Commerce.PaymentMethodResponse>} Information about the payment
     * method that was successfully retrieved.
     *
     */
    getDefaultPaymentMethod() {

        const { commerceManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceApi.name,
            urn: CommerceApiDustUrnReference.getDefaultPaymentMethod
        });

        return commerceManager.getDefaultPaymentMethod().catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @param {String} paymentMethodId Payment method ID retrieved from a request to create a payment method.
     * @desc Retrieves information about a specific card payment method.
     * @throws {PaymentMethodNotFoundException} Unable to retrieve the payment method as it was not found.
     * @note Request to perform operation returned HTTP 404.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Commerce.CardPaymentMethodResponse>} Information about the card payment method that was
     * successfully retrieved.
     *
     */
    async getPaymentCard(paymentMethodId) {

        const errorMessage = {
            method: `${this.constructor.name}.getPaymentCard(paymentMethodId)`,
            get paymentMethodId() {
                return `${this.method} paymentMethodId should be a String with length`;
            }
        };

        nonEmptyString(paymentMethodId, errorMessage.paymentMethodId, InvalidArgumentException);

        const { commerceManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CommerceApi.name,
            urn: CommerceApiDustUrnReference.getPaymentCard
        });

        try {

            return await commerceManager.getPaymentCard(paymentMethodId);

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Array<LineItems>} lineItems
     * @returns {Promise<Array<SDK.Services.Commerce.AmountedLineItem>>}
     *
     */
    processLineItems(lineItems) {

        const amountedLineItems = [];

        for (const lineItem of lineItems) {

            const { attributes, sku } = lineItem;

            amountedLineItems.push(
                new AmountedLineItem({
                    attributes,
                    sku
                })
            );

        }

        return amountedLineItems;

    }

}



// WEBPACK FOOTER //
// ./src/commerce/commerceApi.js